import { ReactElement, memo } from 'react';
import { usePathname, useRouter } from 'next/navigation';
import DropdownComponent from '../dropdown/dropdown.component';
import { NamespacedIntl } from '@spa/lib/i18n/common/NamespacedIntl';
import { Sort } from '../facets/interfaces';
import styles from './sorts.component.module.scss';
import useNamespacedIntl from '@spa/lib/i18n/client/hooks/useNamespacedIntl';
import useRuntimeSearchParams from '@spa/lib/react/hooks/useRuntimeSearchParams';

interface Props {
  readonly id: string;
  readonly sorts: ReadonlyArray<Sort>;
  readonly selectedSortCode: string;
  readonly stretchToParent?: boolean;
}

const DEFAULT_SORT = 'relevance';
const SORT_BY_PARAM = 'sortBy';

function SortsComponent({ id, sorts, selectedSortCode, stretchToParent }: Props): ReactElement {
  const searchParams = useRuntimeSearchParams();
  const pathname = usePathname();
  const router = useRouter();
  const { formatMessage }: NamespacedIntl = useNamespacedIntl('sorts');
  const selectedItemIdx = sorts.findIndex(({ code }) => code === selectedSortCode);
  const currentSortCode = selectedItemIdx >= 0 ? sorts[selectedItemIdx].code : '';
  const initialSelectedItem = selectedItemIdx >= 0 ? selectedItemIdx : 0;

  const updateUrlQuery = (newSortCode: string): string => {
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.delete('page');

    if (newSortCode === DEFAULT_SORT) {
      newSearchParams.delete(SORT_BY_PARAM);
    } else {
      newSearchParams.set(SORT_BY_PARAM, newSortCode);
    }

    const searchParamsSuffix = newSearchParams.size === 0 ? '' : `?${newSearchParams.toString()}`;
    return `${pathname}${searchParamsSuffix}`;
  };

  const onSortChange = (newSortCode: string): void => {
    const updatedUrl = updateUrlQuery(newSortCode);
    void router.push(updatedUrl);
  };

  return (
    <div className={styles.sorts} aria-label="Sort options">
      <DropdownComponent
        id={`${id}-dropdown`}
        stretchToParent={stretchToParent}
        placeholder={
          <span className={'text-base-immutable'}>{formatMessage(currentSortCode)}</span>
        }
        initialSelectedItem={initialSelectedItem}
        onClick={(clickedItemIdx: number): void => {
          const newSort = sorts[clickedItemIdx];
          onSortChange(newSort.code);
        }}
      >
        {sorts.map((sort) => (
          <span className={`${styles['sorts__sort-option']} text-base-immutable`} key={sort.code}>
            {formatMessage(sort.code)}
          </span>
        ))}
      </DropdownComponent>
    </div>
  );
}

export default memo(SortsComponent);
